// ═══════════════════════════════════════════════════════════════
// PLAYBOOK VIEW
// Path: src/views/PlaybookView.jsx
//
// Strategic working guide for directors. Presents the Four P's
// as interactive diagnostic tools, maps NECTAR principles to
// CRM features, and serves as a development reference.
//
// Designed to accommodate additional strategy docs over time.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  BookOpen,
  Users,
  Target,
  TrendingUp,
  Briefcase,
  ArrowRight,
  Flower2,
  Compass,
  ChevronDown,
  ChevronRight,
  Star,
  MessageSquare,
  Eye,
  RefreshCw,
  Shield,
  Layers,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import { Card } from "../components/ui/Card";
import { ActionButton } from "../components/ui/ActionButton";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { HELP } from "../constants/helpContent";

// ── Strategic Content ───────────────────────────────────────
// This structure makes it easy to add new frameworks/docs.
// Each section maps to a strategy document in src/docs/.

const FOUR_PS = [
  {
    id: "personas",
    letter: "P",
    title: "Personas",
    question: "Why does this person give?",
    problem: "We treat everyone the same",
    icon: Users,
    color: "#8b5cf6",
    nectarLink: "C — Cultivation Matched to Persona",
    crmFeature: "Persona system + cultivation guidance",
    crmNav: "donors",
    crmNavLabel: "View Donors",
    principles: [
      "A persona captures the underlying reason a person authorizes your organization to do its work.",
      "Without a persona, every outreach is generic. With a persona, every conversation is guided.",
      "The difference between 'send them the annual appeal' and 'invite them to the peer luncheon because that's what they value' is the difference between fundraising as broadcast and fundraising as relationship.",
    ],
    diagnostic: "Pick any donor in your portfolio. Can you answer, in one sentence, why they give? If you can't, you're cultivating blind.",
  },
  {
    id: "points",
    letter: "P",
    title: "Points",
    question: "How deep is this relationship?",
    problem: "We don't actually know who's engaged",
    icon: TrendingUp,
    color: "#3b82f6",
    nectarLink: "A — Authorization as Center",
    crmFeature: "Engagement scoring + Score Audit",
    crmNav: "cultivation",
    crmNavLabel: "View Pipeline",
    principles: [
      "Inbound contacts are evidence of engagement. The donor chose to invest.",
      "Outbound contacts are evidence of your effort. They prove you're doing your job, not that the relationship is deep.",
      "You can email a donor fifty times and feel productive. That tells you nothing about the relationship. One reply from them tells you everything.",
      "Points decay over time. A relationship that was strong eighteen months ago but has gone silent is not strong today.",
    ],
    diagnostic: "Pick any donor in your portfolio. What's the last inbound contact you received from them, and when? If you have to think about it, the relationship may not be where you assume it is.",
  },
  {
    id: "pathways",
    letter: "P",
    title: "Pathways",
    question: "Where are they in the cultivation arc?",
    problem: "We have no framework for cultivation",
    icon: Compass,
    color: "#10b981",
    nectarLink: "E — Ecosystem Awareness",
    crmFeature: "Cultivation Pipeline + pathway stages",
    crmNav: "cultivation",
    crmNavLabel: "View Pipeline",
    principles: [
      "Prospect → Cold → Connect → Contribute → Lead",
      "Pathways are driven by evidence, not assumption. A donor's position should be determined by their scored engagement — not by the size of their last gift.",
      "A lot of fundraisers carry a mental model of where donors sit, but it's anchored to the last big gift or the last gala conversation, not to a current picture.",
    ],
    diagnostic: "Pick any donor in your portfolio. Where do they sit in your cultivation arc — and is that position based on recent evidence or old assumptions?",
  },
  {
    id: "portfolios",
    letter: "P",
    title: "Portfolios",
    question: "Who owns this relationship?",
    problem: "Nobody is accountable for the next conversation",
    icon: Briefcase,
    color: "#f59e0b",
    nectarLink: "N — Negotiated Relationships",
    crmFeature: "Director Portfolios + assignment",
    crmNav: "portfolios",
    crmNavLabel: "View Portfolios",
    principles: [
      "The moment a relationship belongs to 'the development office,' it belongs to no one.",
      "Assignment is explicit. Every donor is assigned to a specific director.",
      "The portfolio is bounded. A director with twelve assigned relationships can cultivate them with attention.",
      "For every relationship in the portfolio, there is a planned next action. If there isn't, the relationship is drifting.",
    ],
    diagnostic: "Pick any donor in your portfolio. Does someone specific own that relationship? Do they know the persona, the points, and the pathway? If not, the relationship is orphaned regardless of how much money it produced last year.",
  },
];

const NECTAR_PRINCIPLES = [
  { letter: "N", title: "Negotiated Relationships", core: "Authorization is earned, not given. Every philanthropic relationship is a living negotiation, not a transaction.", fourP: "Portfolios", color: "#f59e0b" },
  { letter: "E", title: "Ecosystem Awareness", core: "Donors exist within interconnected systems of motivation and relationship, not in isolation.", fourP: "Pathways", color: "#10b981" },
  { letter: "C", title: "Cultivation Matched to Persona", core: "Different donors authorize different impacts and require different engagement approaches.", fourP: "Personas", color: "#8b5cf6" },
  { letter: "T", title: "Transparency in Outcomes", core: "The self-correcting ethic: flowers that don't provide nectar get abandoned.", fourP: "Points", color: "#3b82f6" },
  { letter: "A", title: "Authorization as Center", core: "Neither donor-centered nor community-centered — authorization-centered.", fourP: "Points", color: "#ec4899" },
  { letter: "R", title: "Renewal Through Delivery", core: "Authorization must be continuously re-earned through demonstrated value.", fourP: "Pathways + Portfolios", color: "#06b6d4" },
];

// ── Component ───────────────────────────────────────────────

export const PlaybookView = () => {
  const { palette, navigate, goBack, goHome, members, directors } = useApp();
  const [activeSection, setActiveSection] = useState("fourps"); // fourps, nectar, diagnostic
  const [expandedP, setExpandedP] = useState(null);
  const [diagnosticDonor, setDiagnosticDonor] = useState(null);
  const [donorSearch, setDonorSearch] = useState("");

  // Quick portfolio health stats
  const portfolioStats = useMemo(() => {
    const total = members.length;
    const assigned = members.filter((m) => m.assignedDirector?.trim()).length;
    const unassigned = total - assigned;
    const donors = members.filter((m) => m.isDonor).length;
    const withPersona = members.filter((m) => m.persona?.trim()).length;
    return { total, assigned, unassigned, donors, withPersona };
  }, [members]);

  // Donor search for diagnostic tool
  const searchResults = useMemo(() => {
    if (!donorSearch || donorSearch.length < 2) return [];
    const q = donorSearch.toLowerCase();
    return members
      .filter((m) =>
        `${m.firstName} ${m.lastName} ${m.organization}`
          .toLowerCase()
          .includes(q)
      )
      .slice(0, 8);
  }, [donorSearch, members]);

  const sectionButtons = [
    { id: "fourps", label: "The Four P's", icon: Star },
    { id: "nectar", label: "NECTAR Framework", icon: Flower2 },
    { id: "diagnostic", label: "Quick Diagnostic", icon: Target },
  ];

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Playbook"
        subtitle="Strategic guide for revenue-building relationships"
        icon={BookOpen}
        helpContent={HELP.playbook}
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />

      {/* Section nav */}
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "10px 24px",
          background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
        }}
      >
        {sectionButtons.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 8,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
              border: "none",
              background: activeSection === s.id ? palette.accent1 : `${palette.accent1}10`,
              color: activeSection === s.id ? "#fff" : palette.accent1,
              transition: "all 0.15s",
            }}
          >
            <s.icon size={ICON_SIZES.sm} />
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>

          {/* ═══ THE FOUR P'S ═══════════════════════════════════ */}
          {activeSection === "fourps" && (
            <div>
              {/* Intro */}
              <Card style={{ marginBottom: 20, padding: 20 }}>
                <div style={{ fontSize: FONT_SIZES.sm, color: palette.text1, fontFamily: FONT_FAMILY, lineHeight: 1.6 }}>
                  <strong>The Four P's</strong> provide a sequential diagnostic that any fundraiser can apply to any relationship
                  in their portfolio. The order matters: understanding precedes measurement, measurement precedes classification,
                  classification precedes accountability.
                </div>
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 16,
                }}>
                  {FOUR_PS.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        textAlign: "center", padding: "8px 4px",
                        borderRadius: 8, background: `${p.color}10`,
                        border: `1px solid ${p.color}30`,
                      }}
                    >
                      <div style={{ fontSize: FONT_SIZES.md, fontWeight: 800, color: p.color, fontFamily: FONT_FAMILY }}>
                        {p.title}
                      </div>
                      <div style={{ fontSize: "0.6rem", color: palette.text2, fontFamily: FONT_FAMILY, marginTop: 2 }}>
                        {p.question}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Each P */}
              {FOUR_PS.map((p) => {
                const isExpanded = expandedP === p.id;
                return (
                  <Card
                    key={p.id}
                    style={{ marginBottom: 12, padding: 0, overflow: "hidden" }}
                  >
                    {/* Header — always visible */}
                    <div
                      onClick={() => setExpandedP(isExpanded ? null : p.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "16px 20px",
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = `${p.color}06`}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <div
                        style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: `${p.color}15`, display: "flex",
                          alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <p.icon size={ICON_SIZES.md} style={{ color: p.color }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                            {p.title}
                          </span>
                          <span style={{
                            fontSize: "0.6rem", fontWeight: 600, padding: "2px 8px",
                            borderRadius: 6, background: `${p.color}15`, color: p.color,
                          }}>
                            {p.problem}
                          </span>
                        </div>
                        <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY, marginTop: 2 }}>
                          {p.question}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <ActionButton
                          label={p.crmNavLabel}
                          icon={ArrowRight}
                          small
                          variant="secondary"
                          onClick={(e) => { e.stopPropagation(); navigate(p.crmNav); }}
                        />
                        {isExpanded
                          ? <ChevronDown size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                          : <ChevronRight size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                        }
                      </div>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${palette.cardBorder}` }}>
                        {/* Principles */}
                        <div style={{ marginTop: 16 }}>
                          <div style={{
                            fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                            color: palette.text2, fontFamily: FONT_FAMILY, marginBottom: 8,
                          }}>
                            Key Principles
                          </div>
                          {p.principles.map((principle, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: "flex", gap: 8, marginBottom: 8,
                                fontSize: FONT_SIZES.xs, color: palette.text1,
                                fontFamily: FONT_FAMILY, lineHeight: 1.5,
                              }}
                            >
                              <div style={{
                                width: 6, height: 6, borderRadius: 3,
                                background: p.color, marginTop: 6, flexShrink: 0,
                              }} />
                              {principle}
                            </div>
                          ))}
                        </div>

                        {/* Diagnostic question */}
                        <div
                          style={{
                            marginTop: 16, padding: "14px 16px",
                            background: `${p.color}08`, border: `1px solid ${p.color}20`,
                            borderRadius: 8,
                          }}
                        >
                          <div style={{
                            fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                            color: p.color, fontFamily: FONT_FAMILY, marginBottom: 6,
                            display: "flex", alignItems: "center", gap: 4,
                          }}>
                            <MessageSquare size={ICON_SIZES.xs} />
                            Diagnostic Question
                          </div>
                          <div style={{
                            fontSize: FONT_SIZES.sm, color: palette.text1,
                            fontFamily: FONT_FAMILY, fontStyle: "italic", lineHeight: 1.5,
                          }}>
                            {p.diagnostic}
                          </div>
                        </div>

                        {/* NECTAR connection */}
                        <div style={{
                          marginTop: 12, fontSize: FONT_SIZES.xs,
                          color: palette.text2, fontFamily: FONT_FAMILY,
                          display: "flex", alignItems: "center", gap: 6,
                        }}>
                          <Flower2 size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
                          NECTAR: <strong style={{ color: palette.text1 }}>{p.nectarLink}</strong>
                          &nbsp;·&nbsp;CRM: <strong style={{ color: palette.text1 }}>{p.crmFeature}</strong>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* ═══ NECTAR FRAMEWORK ═══════════════════════════════ */}
          {activeSection === "nectar" && (
            <div>
              <Card style={{ marginBottom: 20, padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <Flower2 size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
                  <span style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                    NECTAR — Authorization-Based Philanthropy
                  </span>
                </div>
                <div style={{ fontSize: FONT_SIZES.sm, color: palette.text1, fontFamily: FONT_FAMILY, lineHeight: 1.6, marginBottom: 12 }}>
                  Authorization reframes the fundamental nature of the giving relationship. Donors do not simply "give" —
                  they <strong>authorize</strong> an organization's work. This authorization must be continuously earned
                  through genuine value delivery. Like nectar in nature, the value must be real — flowers that advertise
                  nectar they do not provide get abandoned.
                </div>
                <div style={{
                  fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY,
                  padding: "10px 14px", background: `${palette.accent1}06`,
                  borderRadius: 8, fontStyle: "italic", lineHeight: 1.5,
                }}>
                  "Engagement is measured by the donor's investment, not the organization's effort.
                  The scoring philosophy — inbound contacts earn points, outbound contacts don't —
                  is a direct architectural embodiment of this principle."
                </div>
              </Card>

              {NECTAR_PRINCIPLES.map((n) => (
                <Card key={n.letter} style={{ marginBottom: 10, padding: "16px 20px" }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: `${n.color}15`, display: "flex",
                        alignItems: "center", justifyContent: "center",
                        fontSize: FONT_SIZES.lg, fontWeight: 800,
                        color: n.color, fontFamily: FONT_FAMILY, flexShrink: 0,
                      }}
                    >
                      {n.letter}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                        {n.title}
                      </div>
                      <div style={{ fontSize: FONT_SIZES.xs, color: palette.text1, fontFamily: FONT_FAMILY, lineHeight: 1.5, marginTop: 4 }}>
                        {n.core}
                      </div>
                      <div style={{
                        fontSize: "0.65rem", color: palette.text2, fontFamily: FONT_FAMILY,
                        marginTop: 8, display: "flex", alignItems: "center", gap: 4,
                      }}>
                        <ArrowRight size={ICON_SIZES.xs} style={{ color: n.color }} />
                        Expressed through: <strong style={{ color: n.color }}>{n.fourP}</strong>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              {/* Source docs reference */}
              <Card style={{ marginTop: 16, padding: 16 }}>
                <div style={{
                  fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                  color: palette.text2, fontFamily: FONT_FAMILY, marginBottom: 8,
                }}>
                  Source Documents
                </div>
                <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY, lineHeight: 1.6 }}>
                  <div>src/docs/CMM_Four_Ps_Presentation_Reference.md</div>
                  <div>src/docs/NECTAR-Framework-AFP-Application.md</div>
                </div>
              </Card>
            </div>
          )}

          {/* ═══ QUICK DIAGNOSTIC ═══════════════════════════════ */}
          {activeSection === "diagnostic" && (
            <div>
              {/* Portfolio health overview */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Total Donors", value: portfolioStats.donors, color: palette.accent1 },
                  { label: "Assigned", value: portfolioStats.assigned, color: "#10b981" },
                  { label: "Unassigned", value: portfolioStats.unassigned, color: portfolioStats.unassigned > 0 ? "#f59e0b" : "#10b981" },
                  { label: "Have Persona", value: portfolioStats.withPersona, color: "#8b5cf6" },
                ].map((s) => (
                  <Card key={s.label} style={{ textAlign: "center", padding: 14 }}>
                    <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: s.color, fontFamily: FONT_FAMILY }}>
                      {s.value}
                    </div>
                    <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                      {s.label}
                    </div>
                  </Card>
                ))}
              </div>

              {/* Donor lookup */}
              <Card style={{ marginBottom: 16, padding: 20 }}>
                <div style={{
                  fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
                  fontFamily: FONT_FAMILY, marginBottom: 12,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <Target size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
                  Four P's Diagnostic — Select a Donor
                </div>
                <input
                  value={donorSearch}
                  onChange={(e) => { setDonorSearch(e.target.value); setDiagnosticDonor(null); }}
                  placeholder="Search by name or organization..."
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "10px 14px", borderRadius: 8,
                    fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                    outline: "none", background: palette.inputBg,
                    color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`,
                  }}
                />

                {/* Search results */}
                {searchResults.length > 0 && !diagnosticDonor && (
                  <div style={{ marginTop: 8, border: `1px solid ${palette.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
                    {searchResults.map((m) => (
                      <div
                        key={m.id}
                        onClick={() => { setDiagnosticDonor(m); setDonorSearch(`${m.firstName} ${m.lastName}`); }}
                        style={{
                          padding: "8px 14px", cursor: "pointer",
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          transition: "background 0.1s",
                          fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = `${palette.accent1}06`}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <span style={{ fontWeight: 600, color: palette.text1 }}>
                          {m.firstName} {m.lastName}
                        </span>
                        {m.organization && (
                          <span style={{ color: palette.text2 }}> — {m.organization}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Diagnostic results for selected donor */}
              {diagnosticDonor && (
                <Card style={{ padding: 20 }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 16,
                  }}>
                    <div>
                      <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                        {diagnosticDonor.firstName} {diagnosticDonor.lastName}
                      </div>
                      {diagnosticDonor.organization && (
                        <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY }}>
                          {diagnosticDonor.organization}
                        </div>
                      )}
                    </div>
                    <ActionButton
                      label="Open Record"
                      icon={ArrowRight}
                      small
                      onClick={() => navigate("memberDetail", diagnosticDonor)}
                    />
                  </div>

                  {/* Four P's diagnostic cards */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {FOUR_PS.map((p) => {
                      let status = "unknown";
                      let detail = "";

                      if (p.id === "personas") {
                        const hasPersona = diagnosticDonor.persona?.trim();
                        status = hasPersona ? "good" : "gap";
                        detail = hasPersona || "No persona assigned";
                      } else if (p.id === "points") {
                        const score = diagnosticDonor.engagementScore || 0;
                        status = score > 30 ? "good" : score > 0 ? "caution" : "gap";
                        detail = `Engagement score: ${score}`;
                      } else if (p.id === "pathways") {
                        const pathway = diagnosticDonor.pathway || "unengaged";
                        status = pathway === "lead" || pathway === "contribute" ? "good"
                          : pathway === "connect" ? "caution" : "gap";
                        detail = `Pathway: ${pathway}`;
                      } else if (p.id === "portfolios") {
                        const dir = diagnosticDonor.assignedDirector?.trim();
                        status = dir ? "good" : "gap";
                        detail = dir || "Unassigned — no one owns this relationship";
                      }

                      const statusColor = status === "good" ? "#16a34a" : status === "caution" ? "#f59e0b" : "#dc2626";
                      const statusIcon = status === "good" ? "✓" : status === "caution" ? "~" : "✗";

                      return (
                        <div
                          key={p.id}
                          style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "12px 14px", borderRadius: 8,
                            border: `1px solid ${statusColor}30`,
                            background: `${statusColor}06`,
                          }}
                        >
                          <div style={{
                            width: 28, height: 28, borderRadius: 6,
                            background: `${statusColor}15`, display: "flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: FONT_SIZES.sm, fontWeight: 700, color: statusColor,
                          }}>
                            {statusIcon}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: FONT_SIZES.xs, fontWeight: 700,
                              color: palette.text1, fontFamily: FONT_FAMILY,
                            }}>
                              {p.title}: {p.question}
                            </div>
                            <div style={{
                              fontSize: FONT_SIZES.xs, color: palette.text2,
                              fontFamily: FONT_FAMILY, marginTop: 2,
                            }}>
                              {detail}
                            </div>
                          </div>
                          <p.icon size={ICON_SIZES.sm} style={{ color: p.color, opacity: 0.5 }} />
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>

      <ZoneThreeFooter />
    </ViewLayout>
  );
};
